import { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO/SEO';
import TradesPromo from '../components/TradesPromo/TradesPromo';
import { useEtfFlows } from '../hooks/useEtfFlows';
import { usePolymarketWebSocket } from '../hooks/usePolymarketWebSocket';
import { useLivePrices } from '../hooks/useLivePrices';
import { API_BASE_URL } from '../utils/apiConfig';
import { trackLandingFunnel, trackEntryPoint, trackScrollDepth } from '../utils/analytics';
import './LandingPage.css';

function EtfFlowMini() {
  const { history, formatFlow, loading } = useEtfFlows();
  const [selectedDate, setSelectedDate] = useState(null);

  const dateOptions = useMemo(() => {
    if (!history || history.length === 0) return [];
    return history.slice(0, 5);
  }, [history]);

  useEffect(() => {
    if (dateOptions.length > 0 && !selectedDate) {
      setSelectedDate(dateOptions[0].date);
    }
  }, [dateOptions, selectedDate]);

  const selectedData = useMemo(() => {
    if (!selectedDate || !history) return null;
    return history.find(h => h.date === selectedDate);
  }, [selectedDate, history]);

  const barHeights = useMemo(() => {
    if (!selectedData) return { BTC: 0, ETH: 0, SOL: 0 };
    const values = [
      Math.abs(selectedData.BTC || 0),
      Math.abs(selectedData.ETH || 0),
      Math.abs(selectedData.SOL || 0)
    ];
    const maxValue = Math.max(...values, 1);
    return {
      BTC: Math.max((Math.abs(selectedData.BTC || 0) / maxValue) * 100, 5),
      ETH: Math.max((Math.abs(selectedData.ETH || 0) / maxValue) * 100, 5),
      SOL: Math.max((Math.abs(selectedData.SOL || 0) / maxValue) * 100, 5)
    };
  }, [selectedData]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const isPositive = (v) => v !== null && v >= 0;

  if (loading) {
    return <div className="lf-mock lf-etf-mini"><div className="lf-etf-loading">Loading ETF data...</div></div>;
  }

  return (
    <div className="lf-mock lf-etf-mini">
      <div className="lf-etf-mini-dates">
        {dateOptions.map((item) => (
          <button
            key={item.date}
            className={`lf-etf-mini-date-btn ${selectedDate === item.date ? 'active' : ''}`}
            onClick={() => setSelectedDate(item.date)}
          >
            {formatDate(item.date)}
          </button>
        ))}
      </div>
      <div className="lf-etf-mini-chart">
        {['BTC', 'ETH', 'SOL'].map((coin) => (
          <div key={coin} className="lf-etf-mini-col">
            <div className="lf-etf-mini-val-wrap">
              <span className={`lf-etf-mini-val ${isPositive(selectedData?.[coin]) ? 'lf-green' : 'lf-red'}`}>
                {formatFlow(selectedData?.[coin])}
              </span>
            </div>
            <div className="lf-etf-mini-bar-track">
              <div
                className={`lf-etf-mini-bar ${isPositive(selectedData?.[coin]) ? 'lf-etf-mini-bar-pos' : 'lf-etf-mini-bar-neg'}`}
                style={{ height: `${barHeights[coin]}%` }}
              />
            </div>
            <span className="lf-etf-mini-coin">{coin}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PolymarketMini() {
  const { trades, historicalLoading } = usePolymarketWebSocket(
    { minVolume: 50000 },
    { enabled: true }
  );

  const displayTrades = useMemo(() => {
    return trades
      .filter(t => t.dollarSize >= 50000)
      .slice(0, 5);
  }, [trades]);

  const formatAmt = (v) => {
    if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
    if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
    return `$${v.toFixed(0)}`;
  };

  const formatTimeAgo = (ts) => {
    const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
    if (diff < 60) return `${diff}s`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  };

  if (historicalLoading) {
    return <div className="lf-mock lf-poly-mini"><div className="lf-poly-loading">Loading trades...</div></div>;
  }

  return (
    <div className="lf-mock lf-poly-mini">
      <div className="lf-poly-header">
        <span className="lf-poly-label">Whale Trades</span>
        <span className="lf-poly-filter">&gt;$50K</span>
      </div>
      {displayTrades.length === 0 ? (
        <div className="lf-poly-loading">No trades yet</div>
      ) : (
        displayTrades.map((trade, i) => {
          const outcome = trade.outcome || 'Yes';
          const isYes = outcome.toUpperCase() === 'YES';
          const isCustom = !['YES', 'NO'].includes(outcome.toUpperCase());
          const displayOutcome = isCustom
            ? (outcome.length <= 6 ? outcome : outcome.substring(0, 5) + '…')
            : outcome.toUpperCase();
          const title = trade.eventTitle || trade.marketTitle || trade.marketSlug || '';

          return (
            <div key={`${trade.transactionHash || i}-${trade.timestamp}`} className={`lf-poly-row ${i === displayTrades.length - 1 ? 'lf-dim' : ''}`}>
              {trade.marketIcon && <img className="lf-poly-icon" src={trade.marketIcon} alt="" />}
              <span className="lf-poly-title" title={title}>{title}</span>
              <span className="lf-poly-amt">{formatAmt(trade.dollarSize)}</span>
              <span className={`lf-poly-badge ${isCustom ? 'lf-poly-neutral' : isYes ? 'lf-poly-yes' : 'lf-poly-no'}`}>
                {displayOutcome}
              </span>
              <span className="lf-time">{formatTimeAgo(trade.timestamp)}</span>
            </div>
          );
        })
      )}
    </div>
  );
}

function RsiMini() {
  const [coins, setCoins] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/rsi-heatmap`)
      .then(r => r.json())
      .then(data => {
        if (data.success && data.coins) {
          setCoins(data.coins.slice(0, 20));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const getCatClass = (cat) => {
    const map = {
      extremeOversold: 'lf-rsi-deep-green',
      oversold: 'lf-rsi-green',
      neutral: 'lf-rsi-gray',
      overbought: 'lf-rsi-yellow',
      extremeOverbought: 'lf-rsi-red',
    };
    return map[cat] || 'lf-rsi-gray';
  };

  if (loading) {
    return <div className="lf-mock lf-rsi-mini"><div className="lf-rsi-loading">Loading RSI data...</div></div>;
  }

  return (
    <div className="lf-mock lf-rsi-mini">
      <div className="lf-rsi-legend">
        <span className="lf-rsi-leg"><span className="lf-rsi-leg-dot lf-rsi-red" />&gt;80</span>
        <span className="lf-rsi-leg"><span className="lf-rsi-leg-dot lf-rsi-yellow" />70-80</span>
        <span className="lf-rsi-leg"><span className="lf-rsi-leg-dot lf-rsi-gray" />30-70</span>
        <span className="lf-rsi-leg"><span className="lf-rsi-leg-dot lf-rsi-green" />20-30</span>
        <span className="lf-rsi-leg"><span className="lf-rsi-leg-dot lf-rsi-deep-green" />&lt;20</span>
      </div>
      <div className="lf-rsi-grid">
        {coins.map(coin => (
          <div key={coin.symbol} className={`lf-rsi-cell ${getCatClass(coin.category)}`}>
            <span className="lf-rsi-sym">{coin.symbol}</span>
            <span className="lf-rsi-val">{coin.rsi !== null ? coin.rsi.toFixed(0) : '—'}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const CALC_TIMEFRAMES = ['1M', '3M', '6M', '1Y'];

const FAQ_ITEMS = [
  { q: 'Is Tradermap really free?', a: 'Yes. 100% free, forever. No hidden fees, no premium tier, no credit card required. We built it this way intentionally.' },
  { q: 'Do I need to create an account?', a: 'No. Open the terminal and start trading. Zero friction.' },
  { q: 'Where does the data come from?', a: 'Directly from exchanges — Binance, Bybit, OKX, Coinbase, and Hyperliquid. We never modify or delay the data.' },
  { q: 'What is a whale trade?', a: 'A whale trade is a large-volume position opened by an institutional trader or high-net-worth individual. Tracking these can reveal market direction before it becomes obvious to retail traders.' },
  { q: 'What is size distribution?', a: "Size distribution breaks down all trades in the last 24 hours by volume. If trades above $10M are predominantly long, the market tends to continue upward. It's one of the most reliable directional signals on the platform." },
  { q: 'How are liquidations color-coded?', a: 'Red = long liquidation (price dropped, longs got wiped). Green = short liquidation (price rose, shorts got wiped). Simple, fast, actionable.' },
  { q: 'Will Tradermap ever become paid?', a: 'No. Free forever is the plan and the promise.' },
  { q: 'Does Tradermap slow down my computer?', a: 'No. The platform is specifically optimized to run in the background without consuming resources. No ads means no background trackers either.' },
  { q: 'What is the difference between Basic and Terminal?', a: 'Terminal is the full experience — every data stream, every feature, all at once. Basic is a simplified view for those who prefer less information density.' },
];

function CalcMini() {
  const { prices } = useLivePrices();
  const [coin, setCoin] = useState('BTC');
  const [tf, setTf] = useState('1Y');
  const [amount, setAmount] = useState(1000);
  const [histPrices, setHistPrices] = useState({});

  // Fetch historical prices for selected coin
  useEffect(() => {
    if (histPrices[coin]) return;
    fetch(`${API_BASE_URL}/api/historical-prices/${coin}`)
      .then(r => r.json())
      .then(data => {
        if (data.success && data.prices) {
          setHistPrices(prev => ({ ...prev, [coin]: data.prices }));
        }
      })
      .catch(() => {});
  }, [coin, histPrices]);

  const result = useMemo(() => {
    const currentPrice = prices[coin];
    const historicalPrice = histPrices[coin]?.[tf];
    if (!currentPrice || !historicalPrice) return null;

    const priceChange = ((currentPrice - historicalPrice) / historicalPrice) * 100;
    const value = amount * (1 + priceChange / 100);
    const pnl = value - amount;
    return { priceChange, value, pnl, isProfit: pnl >= 0, currentPrice, historicalPrice };
  }, [coin, tf, amount, prices, histPrices]);

  const fmtPrice = (v) => {
    if (!v) return '—';
    if (v >= 1000) return `$${v.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
    if (v >= 1) return `$${v.toFixed(2)}`;
    return `$${v.toPrecision(4)}`;
  };

  const fmtPnl = (v) => {
    if (!v && v !== 0) return '—';
    const sign = v >= 0 ? '+' : '';
    if (Math.abs(v) >= 1000) return `${sign}$${(v).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
    return `${sign}$${v.toFixed(2)}`;
  };

  return (
    <div className="lf-mock lf-calc-mini">
      {/* Coin selector */}
      <div className="lf-calc-coins">
        {['BTC', 'ETH', 'SOL'].map(c => (
          <button key={c} className={`lf-calc-coin-btn ${coin === c ? 'active' : ''}`} onClick={() => setCoin(c)}>{c}</button>
        ))}
      </div>

      {/* Amount input */}
      <div className="lf-calc-input-row">
        <span className="lf-calc-input-label">If you invested</span>
        <div className="lf-calc-input-wrap">
          <span className="lf-calc-input-sign">$</span>
          <input
            className="lf-calc-input"
            type="number"
            value={amount}
            onChange={e => setAmount(Math.max(1, Number(e.target.value)))}
            min="1"
          />
        </div>
      </div>

      {/* Timeframe */}
      <div className="lf-calc-tfs">
        {CALC_TIMEFRAMES.map(t => (
          <button key={t} className={`lf-calc-tf-btn ${tf === t ? 'active' : ''}`} onClick={() => setTf(t)}>{t}</button>
        ))}
      </div>

      {/* Result */}
      <div className="lf-calc-divider" />
      {result ? (
        <div className="lf-calc-results">
          <div className="lf-calc-result-main">
            <span className="lf-calc-result-label">Today it would be worth</span>
            <span className={`lf-calc-result-value ${result.isProfit ? 'lf-green' : 'lf-red'}`}>
              {fmtPrice(result.value)}
            </span>
          </div>
          <div className="lf-calc-result-row">
            <span className="lf-calc-result-label">P&L</span>
            <span className={result.isProfit ? 'lf-green' : 'lf-red'}>{fmtPnl(result.pnl)}</span>
          </div>
          <div className="lf-calc-result-row">
            <span className="lf-calc-result-label">Price change</span>
            <span className={result.isProfit ? 'lf-green' : 'lf-red'}>{result.priceChange >= 0 ? '+' : ''}{result.priceChange.toFixed(1)}%</span>
          </div>
          <div className="lf-calc-result-row lf-dim">
            <span className="lf-calc-result-label">{coin} now</span>
            <span>{fmtPrice(result.currentPrice)}</span>
          </div>
        </div>
      ) : (
        <div className="lf-calc-loading">Loading...</div>
      )}
    </div>
  );
}

function LandingPage() {
  const navigate = useNavigate();
  const scrollDepthTracked = useRef({ 25: false, 50: false, 75: false, 100: false });
  const heroContentRef = useRef(null);
  const scrollArrowRef = useRef(null);
  const bgVideoRef = useRef(null);

  // FAQPage JSON-LD
  useEffect(() => {
    const faqLd = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQ_ITEMS.map(item => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": { "@type": "Answer", "text": item.a }
      }))
    };

    const script = document.createElement('script');
    script.setAttribute('type', 'application/ld+json');
    script.setAttribute('data-schema', 'faq');
    script.textContent = JSON.stringify(faqLd);
    document.head.appendChild(script);

    return () => {
      const el = document.querySelector('script[data-schema="faq"]');
      if (el) el.remove();
    };
  }, []);

  useEffect(() => {
    trackEntryPoint();
    trackLandingFunnel('page_view');

    // Custom scrollbar for landing page on html level
    const style = document.createElement('style');
    style.textContent = 'html{scrollbar-width:thin;scrollbar-color:rgba(255,255,255,0.08) transparent}html::-webkit-scrollbar{width:6px}html::-webkit-scrollbar-track{background:transparent}html::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.08);border-radius:3px}html::-webkit-scrollbar-thumb:hover{background:rgba(255,255,255,0.15)}';
    document.head.appendChild(style);

    return () => {
      style.remove();
    };
  }, []);

  // Scroll-driven parallax: hero slides right + fades, arrow hides
  const handleScroll = useCallback(() => {
    const scrollTop = window.scrollY;
    const vh = window.innerHeight;

    // Hero parallax — complete by 60% of viewport scroll
    const progress = Math.min(scrollTop / (vh * 0.6), 1);

    if (heroContentRef.current) {
      const translateX = progress * 120; // slide right up to 120px
      const opacity = 1 - progress;
      heroContentRef.current.style.transform = `translateX(${translateX}px)`;
      heroContentRef.current.style.opacity = opacity;
    }

    if (scrollArrowRef.current) {
      const arrowOpacity = 1 - Math.min(scrollTop / (vh * 0.15), 1);
      scrollArrowRef.current.style.opacity = arrowOpacity;
    }

    // Scroll depth tracking
    const docHeight = document.documentElement.scrollHeight - vh;
    if (docHeight <= 0) return;
    const scrollPercent = Math.round((scrollTop / docHeight) * 100);

    [25, 50, 75, 100].forEach(threshold => {
      if (scrollPercent >= threshold && !scrollDepthTracked.current[threshold]) {
        scrollDepthTracked.current[threshold] = true;
        trackScrollDepth(threshold, 'landing');
      }
    });
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const handleLaunchApp = (source) => {
    trackLandingFunnel('cta_click', { source });
    navigate('/trades');
  };

  const handleNavClick = (item) => {
    trackLandingFunnel('nav_click', { item });
    if (item === 'web_app') {
      navigate('/trades');
    }
  };

  // Drive the background video from JS instead of the `autoPlay` attribute.
  // The attribute's internal play() promise rejects uncatchably when the
  // browser blocks autoplay (iOS low-power mode, data saver), surfacing as an
  // unhandled NotAllowedError in error tracking. A ref-driven play().catch()
  // swallows the rejection while keeping muted autoplay behaviour everywhere.
  useEffect(() => {
    const v = bgVideoRef.current;
    if (!v) return;
    const p = v.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  }, []);

  return (
    <>
    <SEO
      page="landing"
      title="Real-Time Whale Tracking & Crypto Whale Trades | TraderMap"
      description="The advanced interface for crypto whale tracking. Monitor real-time whale trades on Binance, Bybit, and Hyperliquid. No noise, just data."
      keywords="crypto whale tracking, whale trades, liquidations, crypto analytics, real-time trading, binance, bybit, hyperliquid"
    />
    <div className="landing-page">
      {/* Background Video — fixed behind everything */}
      <video
        ref={bgVideoRef}
        className="landing-background-video"
        loop
        muted
        playsInline
        poster="https://cdn.tradermap.io/landing/room-bg-poster.jpg"
      >
        <source src="https://cdn.tradermap.io/landing/room-bg.webm" type="video/webm" />
        <source src="https://cdn.tradermap.io/landing/room-bg.mp4" type="video/mp4" />
      </video>

      {/* Dark Overlay — fixed */}
      <div className="landing-overlay" />

      {/* Header */}
      <header className="landing-header">
        <nav className="landing-nav">
          <div>
            <img
              src="https://cdn.tradermap.io/landing/trace.svg"
              alt="TraderMap"
              className="landing-logo"
            />
          </div>
          <div className="landing-nav-right">
          </div>
        </nav>
      </header>

      {/* Hero Section — first screen */}
      <section className="landing-hero">
        <div className="landing-hero-content" ref={heroContentRef}>
          {/* Headline */}
          <h1 className="landing-headline">
            <span className="landing-headline-white">Real-Time</span>
            <span className="landing-headline-white">Whale Tracking</span>
          </h1>

          {/* Subtitle */}
          <p className="landing-subtitle">
            Crypto Whale Trades, Liquidations, Prediction Trades and News.
            <br />
            <span className="landing-subtitle-italic">All free, all on one screen.</span>
          </p>

          {/* CTA Button — glass with shimmer border */}
          <div className="landing-cta-wrapper">
            <div className="landing-btn-cta-wrap" onClick={() => handleLaunchApp('hero')}>
              <button className="landing-btn-cta">
                Launch App
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </button>
            </div>
          </div>

          {/* Data source line */}
          <div className="landing-data-source">
            <p className="landing-data-source-line">
              Live data sourced directly from Binance, Bybit, OKX, Coinbase and Hyperliquid with zero delay.
            </p>
          </div>

          {/* Stats */}
          <div className="landing-stats-bar">
            <span className="landing-stat-number">400,726</span>
            <span className="landing-stat-label">trades recorded in the last 24h</span>
          </div>

          {/* Social Proof */}
          <div className="landing-social-proof">
            <span className="landing-stars">&#9733;&#9733;&#9733;&#9733;&#9733;</span>
            <span className="landing-social-proof-text">Loved by traders worldwide</span>
          </div>
        </div>

        {/* Scroll down indicator */}
        <div className="landing-scroll-indicator" ref={scrollArrowRef} aria-hidden="true">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </div>
      </section>

      {/* Live Feed Section */}
      <section className="landing-feed-section">
        <TradesPromo />
      </section>

      {/* Features Section */}
      <section className="landing-features">
        <h2 className="landing-features-heading">Platform Features</h2>

        {/* 1 — ETF Flow Monitor (live data) */}
        <div className="lf-row">
          <div className="lf-visual">
            <EtfFlowMini />
          </div>
          <div className="lf-text">
            <h3>ETF Flow Monitor</h3>
            <p>Track Bitcoin and Ethereum ETF inflows and outflows in real time.</p>
          </div>
        </div>

        {/* 2 — Prediction Trades (live data) */}
        <div className="lf-row lf-row-reverse">
          <div className="lf-visual">
            <PolymarketMini />
          </div>
          <div className="lf-text">
            <h3>Prediction Trades</h3>
            <p>See where institutional players are placing directional bets — before the move happens.</p>
          </div>
        </div>

        {/* 3 — Investment Calculator (live data) */}
        <div className="lf-row">
          <div className="lf-visual">
            <CalcMini />
          </div>
          <div className="lf-text">
            <h3>Investment Calculator</h3>
            <p>Calculate position sizing, PnL, liquidation price and risk/reward ratio instantly.</p>
          </div>
        </div>

        {/* 4 — RSI Heatmap (live data) */}
        <div className="lf-row lf-row-reverse">
          <div className="lf-visual">
            <RsiMini />
          </div>
          <div className="lf-text">
            <h3>RSI Heatmap</h3>
            <p>Multi-timeframe RSI across top assets at a glance — spot overbought and oversold conditions instantly.</p>
          </div>
        </div>

        {/* And more... */}
        <div className="lf-more">
          <h3 className="lf-more-title">And more...</h3>
          <ul className="lf-more-list">
            <li><strong>Real-Time Whale Trade Tracker</strong> — Monitor large institutional positions the moment they're opened across all major exchanges.</li>
            <li><strong>Crypto Liquidation Tracker</strong> — Live long and short liquidations, color-coded for instant directional reading.</li>
            <li><strong>Crypto News Aggregator</strong> — Curated market-moving headlines delivered alongside live trade data.</li>
            <li><strong>Hyperliquid Position Tracker</strong> — Deep visibility into Hyperliquid's on-chain perpetual positions and open interest.</li>
            <li><strong>Size Distribution Analysis</strong> — Breaks down all trades by volume to reveal institutional directional bias.</li>
            <li><strong>Economic Calendar</strong> — Key macro events — CPI, FOMC, NFP — displayed in context with live market data.</li>
          </ul>
        </div>

        {/* CTA */}
        <div className="lf-cta-center">
          <div className="landing-btn-cta-wrap" onClick={() => handleLaunchApp('features')}>
            <button className="landing-btn-cta">
              Try It Now
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="landing-faq">
        <div className="landing-faq-inner">
          <h2 className="landing-faq-heading">Frequently Asked Questions</h2>

          {FAQ_ITEMS.map((item, i) => (
            <details key={i} className="landing-faq-item">
              <summary className="landing-faq-q">{item.q}</summary>
              <p className="landing-faq-a">{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* About Section */}
      <section className="landing-about">
        <div className="landing-about-inner">
          <h2 className="landing-about-title">About TraderMap</h2>
          <div className="landing-about-body">
            <p><strong>Tradermap.io</strong> is a free, real-time crypto market intelligence platform built by traders, for traders.</p>
            <p>We created Tradermap because we were frustrated. Other platforms charge hundreds of dollars per month for data that should be accessible to everyone. So we spent nearly a year optimizing infrastructure to make it completely free, forever.</p>
            <p>Tradermap aggregates live data directly from major exchanges: Binance, Bybit, OKX, Coinbase, and Hyperliquid. Zero delay, zero interference. No algorithms. No manipulation. Raw exchange data, delivered instantly.</p>
            <p>Our terminal gives you everything on a single screen: whale trades, liquidations, ETF flows, prediction trades, size distribution, and crypto news. All in one place, <strong>no ads</strong>, <strong>no sign-up required</strong>.</p>
            <p>We're traders ourselves. We build what we need. And we share it for free.</p>
          </div>
        </div>
      </section>
    </div>
    </>
  );
}

export default LandingPage;
